/**
 * @module Annual Report - Bind Data
 * @description JS for Annual Report Template - Bind Data
 */

const els = {
  map: null,
  tabset: null
}

const state = {
  national: null
}

const formatter = new Intl.NumberFormat( 'en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0
} );


/**
 * @function setData
 * @description Set data for clicked location
 */
export const setData = ( event = null, initTarget = null ) => {
  let location;

  if( event !== null ) {
    const { target } = event;
    location = target.parentNode;

    if( target.tagName === 'circle' ) {
      location = target;
    }

    if( target.tagName === 'SELECT' ) {
      location = target.options[ target.selectedIndex ];
    }
  }

  if( initTarget !== null ) {
    location = initTarget;
  }

  // If they click on state in national view, we need to change the tab
  const view = els.map.getAttribute( 'data-view' );
  const type = location.getAttribute( 'data-location-type' );

  // If they click a state while in metro tab we don't want to do anything
  if( view === 'metro' && type === 'state' ) {
    return;
  }

  // Set active state
  const locations = els.map.querySelectorAll( `[data-location-type="${ type }"]` );
  const siblingLocations = Array.prototype.slice.call( locations ).filter( ( location ) => location.classList.contains( '--is-active' ) );
  siblingLocations.forEach( ( sibling ) => {
    sibling.classList.remove( '--is-active' );
  } );

  location.classList.add( '--is-active' );

  // Switch from national to state if a state is clicked.
  if( view === 'national' && initTarget === null ) {

    if( view !== type ) {
      const newTab = els.tabset.querySelector( `.ins-annual-report__tab-link[data-value="${ type }"]` );
      newTab.click();
    }
  }

  // If were on mobile and they want to see National again we simulate a click.
  if( type === 'national' && initTarget === null ) {
    const nationalTab = els.tabset.querySelector( '.ins-annual-report__tab-link[data-value="national"]' );
    nationalTab.click();

    return;
  }

  const data = getData( location );
  const targetSelect = document.querySelector( `.ins-annual-report__tabs--select-${ type }` );
  const targetTab = els.tabset.querySelector( `#${ type }.ins-annual-report__tab` );
  const targetSlider = targetTab.querySelector( '.selected-region-data__slider' );


  // Setting asterisk for Michigan
  if(data.location === "Michigan"){
    document.querySelectorAll('[data-asterisk]').forEach((asterisk) => {
      asterisk.classList.remove("+display-none");
    })
  } else {
    document.querySelectorAll('[data-asterisk]').forEach((asterisk) => {
      asterisk.classList.add("+display-none");
    })
  }

  // Set our select
  targetSelect.value = data.location_id;

  // Convert our data to numbers
  data.avg_annual_cost = Number( data.avg_annual_cost );
  data.percent_spend = Number( data.percent_spend * 100 ).toFixed( 2 );

  // Rank Label
  targetTab.querySelector( '.ins-annual-report-box__true-cost-rank-value' ).innerHTML = `#${ data.rank }`;

  // Region Name
  targetTab.querySelector( '.ins-annual-report-box__selected-region-name' ).innerHTML = data.location;

  // Average Annual Premium & Percent of Income Spent
  const dataGroup = targetTab.querySelectorAll( '.selected-region-data__single' );

  // dataGroup[ 0 ] = Average Annual Premium
  const avgAnnPrem = dataGroup[ 0 ];
  const avgAnnPremComp = Math.abs( data.avg_annual_cost - state.national.avg_annual_cost );
  const avgAnnPremCompLang = Math.sign( data.avg_annual_cost - state.national.avg_annual_cost ) === -1 ? 'less' : 'more';
  avgAnnPrem.querySelector( '.selected-region-data__single-data-point' ).innerHTML = formatter.format( data.avg_annual_cost );
  avgAnnPrem.querySelector( '.selected-region-data__single-data-disclaimer span' ).innerHTML = `${ formatter.format( avgAnnPremComp ) } ${ avgAnnPremCompLang }`;

  // dataGroup[ 1 ] = Percent of Income Spent
  const percentIncome = dataGroup[ 1 ];
  const percentIncomeComp = Math.abs( data.percent_spend - state.national.percent_spend ).toFixed( 2 );
  const percentIncomeCompLang = Math.sign( data.percent_spend - state.national.percent_spend ) === -1 ? 'less' : 'more';

  percentIncome.querySelector( '.selected-region-data__single-data-point' ).innerHTML = `${ data.percent_spend }%`;
  percentIncome.querySelector( '.selected-region-data__single-data-disclaimer span' ).innerHTML = `${ percentIncomeComp }% ${ percentIncomeCompLang }`;

  const slides = [
    'credit_score',
    'speeding_ticket',
    'car_accident',
    'coverage_lapse',
    'dui_conviction',
    'teen_driver'
  ];

  slides.forEach( ( slide ) => {
    const slideRef = targetSlider.querySelector( `[data-bind="${ slide }"]` );
    const dataGroup = slideRef.querySelectorAll( '.selected-region-data__slide-data-group' );

    // Convert our data to numbers
    data[ `${ slide }` ] = Number( data[ `${ slide }` ] );
    state.national[ `${ slide }` ] = Number( state.national[ `${ slide }` ] );

    // dataGroup[ 0 ] = Added Cost
    const addedCost = dataGroup[ 0 ];
    const locationCost = data[ `${ slide }` ];
    const nationalCost = state.national[ `${ slide }` ];
    const comparisonCost = Math.abs( locationCost - nationalCost );
    const comparisonCostLanguage = Math.sign( locationCost - nationalCost ) === -1 ? 'less' : 'more';

    addedCost.querySelector( '.selected-region-data__slide-data-point' ).innerHTML = formatter.format( locationCost );
    addedCost.querySelector( '.selected-region-data__slide-data-point-comparison span' ).innerHTML = `${ formatter.format( comparisonCost ) } ${ comparisonCostLanguage }`;

    // dataGroup[ 1 ] = Annual Premium
    const annualPremium = dataGroup[ 1 ];
    const locationPremium = data[ `${ slide }` ] + data.avg_annual_cost;
    const nationalPremium = state.national[ `${ slide }` ] + state.national.avg_annual_cost;
    const comparisonPremium = Math.abs( locationPremium - nationalPremium );
    const comparisonPremiumLanguage = Math.sign( locationPremium - nationalPremium ) === -1 ? 'less' : 'more';

    annualPremium.querySelector( '.selected-region-data__slide-data-point' ).innerHTML = formatter.format( locationPremium );
    annualPremium.querySelector( '.selected-region-data__slide-data-point-comparison span').innerHTML = `${ formatter.format( comparisonPremium ) } ${ comparisonPremiumLanguage }`;
  } );
}

/**
 * @function getData
 * @description Get data for clicked location
 */
const getData = ( location ) => {
  const data = location.hasAttribute( 'data-location-data' ) ? JSON.parse( location.getAttribute( 'data-location-data' ) ) : null;

  return data;
}


/**
 * @function setNationalData
 * @description Set national data to use in calculations
 */
const setNationalData = () => {
  const targetTab = els.tabset.querySelector( '#national.ins-annual-report__tab' );
  const targetSlider = targetTab.querySelector( '.selected-region-data__slider' );

  // Convert our data to numbers
  state.national.avg_annual_cost = Number( state.national.avg_annual_cost );
  state.national.percent_spend = Number( state.national.percent_spend * 100 ).toFixed( 2 );

  // Region Name
  targetTab.querySelector( '.ins-annual-report-box__selected-region-name' ).innerHTML = 'United States';

  // Average Annual Premium & Percent of Income Spent
  const dataGroup = targetTab.querySelectorAll( '.selected-region-data__single' );

  // dataGroup[ 0 ] = Average Annual Premium
  const avgAnnPrem = dataGroup[ 0 ];
  avgAnnPrem.querySelector( '.selected-region-data__single-data-point' ).innerHTML = formatter.format( state.national.avg_annual_cost );

  // dataGroup[ 1 ] = Percent of Income Spent
  const percentIncome = dataGroup[ 1 ];
  percentIncome.querySelector( '.selected-region-data__single-data-point' ).innerHTML = `${ state.national.percent_spend }%`;

  const slides = [
    'credit_score',
    'speeding_ticket',
    'car_accident',
    'coverage_lapse',
    'dui_conviction',
    'teen_driver'
  ];

  slides.forEach( ( slide ) => {
    const slideRef = targetSlider.querySelector( `[data-bind="${ slide }"]` );
    const dataGroup = slideRef.querySelectorAll( '.selected-region-data__slide-data-group' );

    // Convert our data to numbers
    state.national[ `${ slide }` ] = Number( state.national[ `${ slide }` ] );

    // dataGroup[ 0 ] = Added Cost
    const addedCost = dataGroup[ 0 ];
    const nationalCost = state.national[ `${ slide }` ];
    addedCost.querySelector( '.selected-region-data__slide-data-point' ).innerHTML = formatter.format( nationalCost );

    // dataGroup[ 1 ] = Annual Premium
    const annualPremium = dataGroup[ 1 ];
    const nationalPremium = state.national[ `${ slide }` ] + state.national.avg_annual_cost;
    annualPremium.querySelector( '.selected-region-data__slide-data-point' ).innerHTML = formatter.format( nationalPremium );
  } );
}


/**
 * @function getNationalData
 * @description Get national data to use in calculations
 */
const getNationalData = () => {
  const location = els.map.querySelector( '[data-location-type="national"]' );
  state.national = location.hasAttribute( 'data-location-data' ) ? JSON.parse( location.getAttribute( 'data-location-data' ) ) : null;
  setNationalData();
}


/**
 * @function bindEvents
 * @description Bind events for this module
 */
const bindEvents = ( target ) => {
  target.addEventListener( 'click', setData );
}

/**
 * @function init
 * @description Kick off this modules functions
 */
// const init = ( map, tabset ) => {
//   els.map = map;
//   els.tabset = tabset;

//   const types = [ 'state', 'metro' ];

//   types.forEach( ( type ) => {
//     const targets = els.map.querySelectorAll( `[data-location-type="${ type }"]` );

//     targets.forEach( ( target ) => {
//       bindEvents( target );

//       const data = getData( target );
//       if( Number( data.rank ) === 1 ) {
//         // We need to give time for national data to come through
//         setTimeout( () => {
//           setData( null, target );
//         }, 2000)
//       }
//     } );
//   } );

//   getNationalData();
// }


const init = ( map, tabset ) => {
  els.map = map;
  els.tabset = tabset;

  const types = [ 'state', 'metro' ];
  types.forEach( ( type ) => {
    const targets = Array.from(els.map.querySelectorAll( `[data-location-type="${ type }"]` ));
    targets.
      filter((target) => {
        const data = getData( target );
        return data !== null
      }).forEach( ( target ) => {
          bindEvents( target );
          const data = getData( target );
          if( Number( data.rank ) === 1 ) {
            // We need to give time for national data to come through
            setTimeout( () => {
              setData( null, target );
            }, 2000)
          }
      });
  } );

  getNationalData();
}

export default init;
